import { FlightsClassTypes } from 'constants/flight'
import { REWARD_PREVIEW_RATE_BY_CABIN } from 'lib/maldivesCharter/classicRewardPricing'
import type { MaldivesRewardsPayTierPricing } from 'loyalty/lib/maldivesRewardsPayCalculator/computeMaldivesRewardsPayCalculator'

/** Cabins ordered by ascending pay-in-full price (Economy → Economy Extra → Economy Plus → Business). */
export const MALDIVES_REWARDS_PAY_CALCULATOR_CABINS = [
  FlightsClassTypes.ECONOMY,
  FlightsClassTypes.LUXROW,
  FlightsClassTypes.PREMIUM,
  FlightsClassTypes.BUSINESS,
] as const

export type MaldivesRewardsPayCalculatorCabin = typeof MALDIVES_REWARDS_PAY_CALCULATOR_CABINS[number]

/** Hardcoded hotel-only per-person package price shown in the pay calculator. */
export const MALDIVES_REWARDS_PAY_CALCULATOR_HOTEL_PP = 1100

/** Pay-in-full per-person anchors from the intro comparison table. */
const PAY_IN_FULL_PP_BY_CABIN: Record<MaldivesRewardsPayCalculatorCabin, number> = {
  [FlightsClassTypes.ECONOMY]: 2299,
  [FlightsClassTypes.PREMIUM]: 2799,
  [FlightsClassTypes.LUXROW]: 2599,
  [FlightsClassTypes.BUSINESS]: 6699,
}

function buildTierPricing(cabin: MaldivesRewardsPayCalculatorCabin): MaldivesRewardsPayTierPricing {
  const flight = REWARD_PREVIEW_RATE_BY_CABIN[cabin]!
  const hotelPP = MALDIVES_REWARDS_PAY_CALCULATOR_HOTEL_PP

  return {
    hotelPP,
    coPaymentPP: flight.cashPerPerson,
    cashPP: hotelPP + flight.cashPerPerson,
    ptsPP: flight.pointsPerPerson,
    fullPP: PAY_IN_FULL_PP_BY_CABIN[cabin],
  }
}

/**
 * Illustrative per-cabin pricing for the Société Rewards pay calculator.
 * Flight points and co-payment use {@link REWARD_PREVIEW_RATE_BY_CABIN} — not charter search.
 */
export const MALDIVES_REWARDS_PAY_CALCULATOR_TIER_PRICING_BY_CABIN: Record<
  MaldivesRewardsPayCalculatorCabin,
  MaldivesRewardsPayTierPricing
> = {
  [FlightsClassTypes.ECONOMY]: buildTierPricing(FlightsClassTypes.ECONOMY),
  [FlightsClassTypes.PREMIUM]: buildTierPricing(FlightsClassTypes.PREMIUM),
  [FlightsClassTypes.LUXROW]: buildTierPricing(FlightsClassTypes.LUXROW),
  [FlightsClassTypes.BUSINESS]: buildTierPricing(FlightsClassTypes.BUSINESS),
}

function useMaldivesRewardsPayCalculatorPricing(): {
  tierPricingByCabin: Record<MaldivesRewardsPayCalculatorCabin, MaldivesRewardsPayTierPricing>
  fetching: boolean
} {
  return {
    tierPricingByCabin: MALDIVES_REWARDS_PAY_CALCULATOR_TIER_PRICING_BY_CABIN,
    fetching: false,
  }
}

export default useMaldivesRewardsPayCalculatorPricing
